import { useState, useCallback, useRef } from 'react';
import configService from '../services/configService';

/**
 * Hook personalizado para gestión de configuraciones del sistema
 * Maneja estado, filtros y operaciones CRUD
 */
export const useConfigManagement = () => {
  // Estados principales
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Estados de filtros
  const [filters, setFilters] = useState({
    search: '',
    isActive: undefined
  });

  // Referencia para cancelar requests pendientes
  const abortControllerRef = useRef(null);

  /**
   * Limpiar error
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Aplicar filtros de configuración
   */
  const applyConfigFilters = useCallback((newFilters) => {
    setFilters(newFilters);
  }, []);

  /**
   * Limpiar filtros
   */
  const clearFilters = useCallback(() => {
    setFilters({
      search: '',
      isActive: undefined
    });
  }, []);

  /**
   * Obtener configuraciones con filtros
   */
  const fetchConfigs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Cancelar request anterior si existe
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Crear nuevo AbortController
      abortControllerRef.current = new AbortController();

      // Preparar parámetros de búsqueda
      const params = {};
      
      if (filters.search) {
        params.search = filters.search;
      }
      
      if (filters.isActive !== undefined) {
        params.active = filters.isActive;
      }

      const data = await configService.getConfigs(params);
      setConfigs(data);
    } catch (error) {
      if (error.name !== 'AbortError') {
        setError(error.message);
        console.error('Error fetching configs:', error);
      }
    } finally {
      setLoading(false);
      abortControllerRef.current = null;
    }
  }, [filters]);

  /**
   * Aplicar filtros ahora (ejecutar búsqueda)
   */
  const applyFiltersNow = useCallback(async () => {
    await fetchConfigs();
  }, [fetchConfigs]);

  /**
   * Obtener configuraciones activas
   */
  const fetchActiveConfigs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await configService.getActiveConfigs();
      setConfigs(data);
    } catch (error) {
      setError(error.message);
      console.error('Error fetching active configs:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Buscar configuraciones por clave
   */
  const searchConfigsByKey = useCallback(async (key) => {
    try {
      setLoading(true);
      setError(null);

      const data = await configService.searchConfigsByKey(key);
      setConfigs(data);
    } catch (error) {
      setError(error.message);
      console.error('Error searching configs:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Obtener configuración por ID
   */
  const getConfigById = useCallback(async (id) => {
    try {
      setLoading(true);
      setError(null);

      const data = await configService.getConfigById(id);
      return data;
    } catch (error) {
      setError(error.message);
      console.error('Error getting config by ID:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Obtener configuración por clave
   */
  const getConfigByKey = useCallback(async (key) => {
    try {
      setLoading(true);
      setError(null);

      const data = await configService.getConfigByKey(key);
      return data;
    } catch (error) {
      setError(error.message);
      console.error('Error getting config by key:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Crear nueva configuración
   */
  const createConfig = useCallback(async (configData) => {
    try {
      setLoading(true);
      setError(null);

      const newConfig = await configService.createConfig(configData);
      
      // Actualizar lista local
      setConfigs(prev => [newConfig, ...prev]);
      
      return newConfig;
    } catch (error) {
      setError(error.message);
      console.error('Error creating config:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Actualizar configuración
   */
  const updateConfig = useCallback(async (id, configData) => {
    try {
      setLoading(true);
      setError(null);

      const updatedConfig = await configService.updateConfig(id, configData);
      
      // Actualizar lista local
      setConfigs(prev => prev.map(config => 
        config.id === id ? updatedConfig : config
      ));
      
      return updatedConfig;
    } catch (error) {
      setError(error.message);
      console.error('Error updating config:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Actualizar configuración por clave
   */
  const updateConfigByKey = useCallback(async (key, value) => {
    try {
      setLoading(true);
      setError(null);

      const updatedConfig = await configService.updateConfigByKey(key, value);
      
      // Actualizar lista local
      setConfigs(prev => prev.map(config => 
        config.key === key ? updatedConfig : config
      ));
      
      return updatedConfig;
    } catch (error) {
      setError(error.message);
      console.error('Error updating config by key:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Cambiar estado activo/inactivo de configuración
   */
  const toggleConfigActive = useCallback(async (id) => {
    try {
      setLoading(true);
      setError(null);

      const updatedConfig = await configService.toggleConfigActive(id);
      
      // Actualizar lista local
      setConfigs(prev => prev.map(config => 
        config.id === id ? updatedConfig : config
      ));
      
      return updatedConfig;
    } catch (error) {
      setError(error.message);
      console.error('Error toggling config active:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Eliminar configuración
   */
  const deleteConfig = useCallback(async (id) => {
    try {
      setLoading(true);
      setError(null);

      await configService.deleteConfig(id);
      
      // Actualizar lista local
      setConfigs(prev => prev.filter(config => config.id !== id));
      
      return true;
    } catch (error) {
      setError(error.message);
      console.error('Error deleting config:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Obtener configuraciones públicas
   */
  const getPublicConfigs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await configService.getPublicConfigs();
      return data;
    } catch (error) {
      setError(error.message);
      console.error('Error getting public configs:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Obtener configuraciones de autenticación
   */
  const getAuthConfigs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await configService.getAuthConfigs();
      return data;
    } catch (error) {
      setError(error.message);
      console.error('Error getting auth configs:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    // Estados
    configs,
    loading,
    error,
    filters,
    
    // Métodos de filtrado
    applyConfigFilters,
    clearFilters,
    applyFiltersNow,
    
    // Métodos de obtención
    fetchConfigs,
    fetchActiveConfigs,
    searchConfigsByKey,
    getConfigById,
    getConfigByKey,
    getPublicConfigs,
    getAuthConfigs,
    
    // Métodos CRUD
    createConfig,
    updateConfig,
    updateConfigByKey,
    toggleConfigActive,
    deleteConfig,
    
    // Utilidades
    clearError
  };
}; 